import { EventEmitter, Input, Output, Directive, ViewChild, ElementRef, OnInit, AfterViewInit } from '@angular/core';
import { ChildTableColumn } from '../../core/models/table.models';

/**
 * Shared base for all cell editors.
 * Each editor emits save (confirm), cancel (revert), and tabSave (tab to next cell).
 */
@Directive()
export abstract class BaseCellEditor {
  @Input() column!: ChildTableColumn<any>;
  @Input() value: any;
  @Input() rowId = '';
  @Input() editHintId = '';
  @Input() hasError = false;
  @Input() isRequired = false;
  @Input() errorId = '';

  get ariaDescribedBy(): string {
    const parts: string[] = [];
    if (this.hasError && this.errorId) parts.push(this.errorId);
    if (this.editHintId) parts.push(this.editHintId);
    return parts.join(' ') || null as any;
  }

  @Output() save = new EventEmitter<any>();
  @Output() cancel = new EventEmitter<void>();
  @Output() tabSave = new EventEmitter<{ value: any; shiftKey: boolean }>();

  editingValue: any;

  protected onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.save.emit(this.editingValue);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      this.cancel.emit();
    } else if (event.key === 'Tab') {
      event.preventDefault();
      event.stopPropagation();
      this.tabSave.emit({ value: this.editingValue, shiftKey: event.shiftKey });
    }
  }
}

/**
 * Shared base for simple input cell editors (text, email, number, phone).
 */
@Directive()
export abstract class BaseInputEditor extends BaseCellEditor implements OnInit, AfterViewInit {
  @ViewChild('editInput') editInput!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.editingValue = this.value ?? '';
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.editInput?.nativeElement?.focus();
      this.editInput?.nativeElement?.select();
    });
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.editInput?.nativeElement) {
      this.syncValue(this.editInput.nativeElement.value);
    }
    super.onKeydown(event);
  }

  protected syncValue(val: string): void {
    this.editingValue = val;
  }
}

/**
 * Shared logic for listbox-based editors (select, multiselect).
 */
@Directive()
export abstract class BaseListboxEditor extends BaseCellEditor {
  highlightIndex = 0;

  protected abstract get a11y(): any;

  protected handleListboxNavigation(event: KeyboardEvent, options: any[]): boolean {
    if (!options?.length) return false;
    
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.highlightIndex = Math.min(this.highlightIndex + 1, options.length - 1);
        this.a11y.announce(options[this.highlightIndex].label);
        return true;
      case 'ArrowUp':
        event.preventDefault();
        this.highlightIndex = Math.max(this.highlightIndex - 1, 0);
        this.a11y.announce(options[this.highlightIndex].label);
        return true;
      case 'Home':
        event.preventDefault();
        this.highlightIndex = 0;
        this.a11y.announce(options[0].label);
        return true;
      case 'End':
        event.preventDefault();
        this.highlightIndex = options.length - 1;
        this.a11y.announce(options[this.highlightIndex].label);
        return true;
    }
    return false;
  }
}
